import { cn } from '@/lib/utils';
import { brand } from '@/theme';
import { SignPanel } from './SignPanel';

type BrandProps = {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /**
   * Override the wordmark text size (Tailwind classes). Use for tight
   * containers like the nav, where the default `size` text would overflow.
   * Defaults to the sizeMap value for the chosen `size`.
   */
  textClassName?: string;
  /** Override the displayed wordmark text. Defaults to `brand.appName`. */
  children?: string;
};

const sizeMap = {
  sm: 'text-2xl',
  md: 'text-4xl',
  lg: 'text-5xl md:text-6xl',
  xl: 'text-6xl md:text-8xl',
};

const panelSize: Record<NonNullable<BrandProps['size']>, 'sm' | 'md' | 'lg'> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'lg',
};

/**
 * The app's hero wordmark — Vegas-sign lettering on a lit cream panel.
 * The marquee text is locked to a single line (`whitespace-nowrap`) so
 * "PARKS SLOTS" never stacks onto two lines, no matter how narrow the
 * container.
 */
export function Brand({ size = 'md', className, textClassName, children }: BrandProps) {
  return (
    <SignPanel size={panelSize[size]} className={className}>
      <h1
        className={cn(
          'text-marquee uppercase select-none leading-none whitespace-nowrap',
          textClassName ?? sizeMap[size],
        )}
      >
        {children ?? brand.appName}
      </h1>
    </SignPanel>
  );
}
